// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject, type WheelEvent as ReactWheelEvent } from 'react';

import type { Stair } from '../../api/types';
import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { useCanEdit } from '../../app/CaseRoleContext';
import { kindOf } from '../../app/keyTargets';
import { guideSnaps } from '../../bygning/cadMeasure';
import { lengthInput, parseLength } from '../../bygning/geometriEdit';
import { flushKeys, resolveKeys, toolHint } from '../../bygning/geometriKeys';
import { infer, manhattanAngle, snapOffset, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import type { TrackPoint } from '../../bygning/smartGuides';
import {
  applyStairOps,
  cmText,
  comfort,
  comfortOk,
  createStairOps,
  defaultRisers,
  rederiveHint,
  runFrame,
  stairFootprint,
  stairGripEdit,
  stairGrips,
  stairLabel,
  stairRise,
  stairSymbol,
  type StairGrip,
  type StairLayout,
  type StairSymbol,
} from '../../bygning/stairs';
import { EMPTY_DRAW, clickStair, draftOf, readout, type StairDraw, type StairDrawContext, type StairTyped } from '../../bygning/stairTool';
import { CadIcon } from '../../components/bygning/rediger/cadIcons';
import type { ViewportHandle } from '../../viewport/Viewport';
import { Glyph, ScreenTip, TrackLines } from './GeometriLayer';
import { useSmartTracking } from './useSmartTracking';
import styles from './GeometriLayer.module.css';
import flips from './OpeningLayer.module.css';
import own from './StairLayer.module.css';

/** The inference engine's reach, px. */
const SNAP_PX = 12;
/** A press that moves less than this (px) is a click. */
const DRAG_PX = 4;

const FIELDS = ['Trin', 'Længde'] as const;

export interface StairLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

/** SketchUp's measurement box: the typed fields and the one being typed. */
interface Typed {
  fields: string[];
  at: number;
}

/** A grip drag on a selected stair. */
interface Drag {
  grip: StairGrip;
  stair: Stair;
  moved: boolean;
  inf: Inference | null;
  result: ReturnType<typeof stairGripEdit> | null;
}

const fmt = (v: number) => lengthInput(v);
const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];

function typedValues(t: Typed | null): StairTyped | null {
  if (!t) return null;
  const out: StairTyped = {};
  const n = (t.fields[0] ?? '').trim();
  if (/^\d+$/.test(n) && Number(n) >= 1) out.risers = Number(n);
  const L = t.fields[1] ? parseLength(t.fields[1]) : null;
  if (L !== null && L > 0) out.length = L;
  return out.risers === undefined && out.length === undefined ? null : out;
}

/**
 * Stairs on the locked plan (frontend-u0o.10, slice S8; mock 06), beside
 * `GeometriLayer`, `CadToolLayer`, `OpeningLayer` and `RoomLayer`:
 *
 *  - every stair on the shown storey by Danish drawing practice (a line per
 *    riser, the walking line with its dot and arrow, the break line at the
 *    plan cut, "OP"); a stair arriving from below shows its hole, crossed;
 *  - the Trappe tool (T): by run — the first click is the bottom riser, run
 *    1 grows with the pointer in whole goings ("9 af 16 trin"), digits set
 *    the riser count (Tab: the run length); a click short of the risers
 *    lays a landing and run 2 turns to the pointer's side (Mellemrum
 *    flips it); the stair is one batch, one undo;
 *  - a selected stair's grips (the top end stretches the going; run 1's end
 *    moves the landing, risers trading between the runs; the outer edge
 *    sets the width) and its Vend control (Mellemrum).
 */
export function StairLayer({ viewport, host: hostEl, editor, storeyId, z }: StairLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [draw, setDrawState] = useState<StairDraw>(EMPTY_DRAW);
  const [pointer, setPointerState] = useState<{ raw: XY; inf: Inference | null } | null>(null);
  const [typed, setTypedState] = useState<Typed | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const live = useRef({ editor, storeyId, z, draw, pointer, typed, drag });
  live.current = { editor, storeyId, z, draw, pointer, typed, drag };
  const setDraw = (d: StairDraw) => {
    live.current.draw = d;
    setDrawState(d);
  };
  const setPointer = (p: typeof pointer) => {
    live.current.pointer = p;
    setPointerState(p);
  };
  const setTyped = (t: Typed | null) => {
    live.current.typed = t;
    setTypedState(t);
  };
  const setDrag = (d: Drag | null) => {
    live.current.drag = d;
    setDragState(d);
  };

  const canEdit = useCanEdit();
  const model = editor.model;
  const opts = editor.stairOptions;
  const toolOn = editor.tool === 'stair' && editor.modify === null && canEdit && storeyId !== null;

  // ------------------------------------------------------- projection --
  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: XY): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );
  const planAtClient = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );

  // ------------------------------------------------------------ rise --
  const rise = useMemo(() => stairRise(model.storeys, storeyId, opts.toStorey), [model.storeys, storeyId, opts.toStorey]);
  const ctx = useMemo<StairDrawContext | null>(
    () => (rise && rise.zTop > rise.zBase ? { total: defaultRisers(rise.zTop - rise.zBase), going: opts.going, width: opts.width, location: opts.location, mode: opts.mode } : null),
    [rise, opts.going, opts.width, opts.location, opts.mode],
  );
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const riseRef = useRef(rise);
  riseRef.current = rise;

  // -------------------------------------------------- inference input --
  // A stair stands against wall faces: both faces of every wall on the storey, the traces and the guides.
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
      if (!(L > 1e-6)) continue;
      const h = (w.thickness ?? 0) / 2;
      const n: XY = [(-(w.end[1] - w.start[1]) / L) * h, ((w.end[0] - w.start[0]) / L) * h];
      for (const k of h > 0 ? [1, -1] : [0]) {
        out.push({ a: [w.start[0] + n[0] * k, w.start[1] + n[1] * k], b: [w.end[0] + n[0] * k, w.end[1] + n[1] * k], source: 'wall', label: `Væg ${w.id} · flade`, guid: w.guid });
      }
    }
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [model.walls, snap, storeyId]);
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const snapRef = useRef({ segments, guides, axisAngle });
  snapRef.current = { segments, guides, axisAngle };
  const { tracked, points: trackedNow, observe } = useSmartTracking(`${editor.tool}:${opts.mode}`);
  const inferAt = useCallback(
    (raw: XY, origin: XY | null, alt: boolean, auto: readonly TrackPoint[] = []): Inference => {
      const c = snapRef.current;
      const inf = infer(raw, {
        segments: [...c.segments, ...c.guides.segments],
        points: c.guides.points,
        axisAngle: c.axisAngle,
        origin,
        toggles: live.current.editor.snap,
        tolerance: metresPer(SNAP_PX),
        suspended: alt,
        tracked: [...trackedNow(), ...auto],
      });
      observe(inf);
      return inf;
    },
    [metresPer, trackedNow, observe],
  );

  // A new tool, mode or storey starts afresh.
  useEffect(() => {
    setDraw(EMPTY_DRAW);
    setTyped(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.tool, opts.mode, storeyId]);

  // ---------------------------------------------------------- commit --
  const finish = useCallback((layout: StairLayout) => {
    const { editor: ed, storeyId: sid } = live.current;
    const r = riseRef.current;
    if (sid === null || !r) return;
    const guid = ed.newGuid();
    const o = ed.stairOptions;
    const made = createStairOps({ storeyId: sid, rise: r, layout, mode: o.mode, location: o.location, cutSlab: o.cutSlab, guid });
    if ('error' in made) {
      ed.notify(made.error);
      return;
    }
    void ed.commit(made.ops, made.label).then((ok) => {
      if (!ok) return;
      const now = live.current.editor;
      const rec = made.ops[0] && 'record' in made.ops[0] ? made.ops[0].record : null;
      const hint = rec ? rederiveHint(now.model, { cut_slab_above: rec.cut_slab_above === true, storey_to_id: typeof rec.storey_to_id === 'number' ? rec.storey_to_id : null }) : null;
      now.notify(hint ? `Ny trappe · ${hint}` : 'Ny trappe');
      now.setTool('select');
      now.select([guid]);
    });
  }, []);

  const clickAt = useCallback(
    (p: XY) => {
      const c = ctxRef.current;
      if (!c) {
        live.current.editor.notify('Vælg en etage med en højde for at tegne en trappe');
        return;
      }
      const r = clickStair(live.current.draw, c, p, typedValues(live.current.typed));
      setTyped(null);
      setDraw(r.draw);
      if (r.commit) {
        setPointer(null);
        finish(r.commit);
      }
    },
    [finish],
  );

  // ------------------------------------------------ the tool's pointer --
  useEffect(() => {
    const el = hostEl.current;
    if (!el || !toolOn) return;
    const resolve = (e: PointerEvent): { raw: XY; inf: Inference | null } | null => {
      const raw = planAtClient(e.clientX, e.clientY);
      if (!raw) return null;
      const d = live.current.draw;
      // Run 2 only picks a side: no snapping.
      if (d.first !== null) return { raw, inf: null };
      const corners: TrackPoint[] = d.start ? [{ p: d.start, what: 'endepunkt', of: 'Ny trappe', auto: true }] : [];
      return { raw, inf: inferAt(raw, d.start, e.altKey, corners) };
    };
    const onMove = (e: PointerEvent) => {
      const s = resolve(e);
      if (!s) return;
      setPointer(s);
      live.current.editor.cursor.set(s.inf?.point ?? s.raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const s = resolve(e);
      if (!s) return;
      setPointer(s);
      clickAt(s.inf?.point ?? s.raw);
    };
    const onLeave = () => setPointer(null);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointerleave', onLeave);
    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointerleave', onLeave);
      el.style.cursor = prevCursor;
      setPointer(null);
    };
  }, [hostEl, toolOn, planAtClient, inferAt, clickAt]);

  // ------------------------------------------------- a selected stair --
  const storeyStairs = useMemo(() => (model.stairs ?? []).filter((s) => storeyId === null || s.storey_id === storeyId), [model.stairs, storeyId]);
  const arriving = useMemo(() => (model.stairs ?? []).filter((s) => storeyId !== null && s.storey_to_id === storeyId && s.storey_id !== storeyId && s.cut_slab_above), [model.stairs, storeyId]);
  const selectedSet = useMemo(() => new Set(editor.selection), [editor.selection]);
  const selectOn = editor.tool === 'select' && !editor.modify;
  const selected = selectOn && editor.selection.length === 1 ? (storeyStairs.find((s) => s.guid === editor.selection[0]) ?? null) : null;
  const grips = selected && canEdit ? stairGrips(selected) : [];

  const flipSelected = useCallback(() => live.current.editor.run('flip'), []);

  // ------------------------------------------------------------- keys --
  const keysOn = toolOn || (selected !== null && canEdit);
  useEffect(() => {
    if (!keysOn) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, [role="dialog"], [role="menu"]')) return;
      // A focused button keeps its Space (it presses it); every other key is the tool's.
      if (e.key === ' ' && target?.closest('button')) return;
      if (live.current.drag) return;
      const { editor: ed, draw: d, typed: t, pointer: p } = live.current;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
      if (ed.tool !== 'stair') {
        // A selected stair: Mellemrum turns it round (Revit's flip control).
        if (e.key === ' ' && plain && ed.tool === 'select') {
          stop();
          ed.run('flip');
        }
        return;
      }
      if (e.key === 'Escape') {
        stop();
        if (t) setTyped(null);
        else if (d.first !== null && d.start) setDraw({ ...EMPTY_DRAW, start: d.start });
        else if (d.start) setDraw(EMPTY_DRAW);
        else ed.setTool('select');
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (d.start && (p || t)) clickAt(p ? (p.inf?.point ?? p.raw) : d.start);
        return;
      }
      if (e.key === ' ' && plain) {
        stop();
        if (d.first !== null) setDraw({ ...d, flip: !d.flip });
        return;
      }
      if (e.key === 'Tab' && d.start && d.first === null) {
        stop();
        const cur = t ?? { fields: [], at: 0 };
        setTyped({ ...cur, at: e.shiftKey ? (cur.at + FIELDS.length - 1) % FIELDS.length : (cur.at + 1) % FIELDS.length });
        return;
      }
      if ((e.key === 'Backspace' || e.key === 'Delete') && plain && d.start) {
        stop();
        if (t) {
          const next = [...t.fields];
          next[t.at] = (next[t.at] ?? '').slice(0, -1);
          setTyped(next.every((x) => !x) ? null : { ...t, fields: next });
        } else if (d.first !== null) setDraw({ ...EMPTY_DRAW, start: d.start });
        else setDraw(EMPTY_DRAW);
        return;
      }
      if (/^[0-9.,]$/.test(e.key) && plain && d.start && d.first === null) {
        stop();
        const cur = t ?? { fields: [], at: 0 };
        const next = [...cur.fields];
        // Trin takes whole numbers only.
        if (cur.at === 0 && !/^[0-9]$/.test(e.key)) return;
        next[cur.at] = (next[cur.at] ?? '') + e.key;
        setTyped({ ...cur, fields: next });
        return;
      }
      if (d.start) {
        // Mid-stair: no tool switch on a stray letter.
        if (e.key.length === 1 && plain) stop();
        return;
      }
      // The rest is the editor's (tools' letters, ⌘Z): a letter acts alone.
      const out = resolveKeys({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) }, '');
      const cmds = out.buffer ? [flushKeys(out.buffer)].filter((c) => c !== null) : out.commands;
      const run = cmds.filter((c) => c !== 'escape' && c !== 'cycle' && c !== 'cycle-back');
      if (run.length > 0 || (e.key.length === 1 && plain)) stop();
      for (const c of run) ed.run(c);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [keysOn, clickAt]);

  // ------------------------------------------------------------ grips --
  const startGrip = (grip: StairGrip, s: Stair, e: ReactPointerEvent) => {
    if (e.button !== 0 || !canEdit) return;
    e.stopPropagation();
    e.preventDefault();
    const x0 = e.clientX;
    const y0 = e.clientY;
    setDrag({ grip, stair: s, moved: false, inf: null, result: null });
    const move = (m: PointerEvent) => {
      const cur = live.current.drag;
      if (!cur) return;
      if (!cur.moved && Math.hypot(m.clientX - x0, m.clientY - y0) < DRAG_PX) return;
      const raw = planAtClient(m.clientX, m.clientY);
      if (!raw) return;
      const g = cur.grip;
      if (g.kind === 'width') {
        // The edge moves square to itself: it snaps to a parallel wall face, trace or guide.
        const c = snapRef.current;
        const rawOff = (raw[0] - g.at[0]) * g.axis[0] + (raw[1] - g.at[1]) * g.axis[1];
        const snapped = snapOffset(g.at, g.axis, rawOff, { segments: [...c.segments, ...c.guides.segments], toggles: live.current.editor.snap, tolerance: metresPer(SNAP_PX), suspended: m.altKey });
        const p: XY = [g.at[0] + g.axis[0] * snapped.offset, g.at[1] + g.axis[1] * snapped.offset];
        setDrag({ ...cur, moved: true, inf: snapped.inference, result: stairGripEdit(cur.stair, g, p) });
        return;
      }
      // An end moves along its run: the snap's point, projected onto it (stairGripEdit).
      const inf = inferAt(raw, g.at, m.altKey);
      setDrag({ ...cur, moved: true, inf, result: stairGripEdit(cur.stair, g, inf.kind === 'free' ? raw : inf.point) });
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('keydown', esc, true);
    };
    const up = () => {
      end();
      const cur = live.current.drag;
      setDrag(null);
      if (!cur?.moved || !cur.result) return;
      const ed = live.current.editor;
      if ('error' in cur.result) {
        ed.notify(cur.result.error);
        return;
      }
      const what = cur.grip.kind === 'width' ? 'Bredde' : cur.grip.kind === 'first' ? 'Repos' : 'Grund';
      void ed.commit(cur.result.ops, `${what} · ${stairLabel(cur.stair)}`).then((ok) => {
        const hint = ok ? rederiveHint(live.current.editor.model, cur.stair) : null;
        if (hint) live.current.editor.notify(`${stairLabel(cur.stair)} ændret · ${hint}`);
      });
    };
    const cancel = () => {
      end();
      setDrag(null);
    };
    const esc = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') return;
      k.preventDefault();
      k.stopPropagation();
      cancel();
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('keydown', esc, true);
  };
  useEffect(() => {
    if (live.current.drag && live.current.drag.stair.guid !== selected?.guid) setDrag(null);
  }, [selected?.guid]);

  // ------------------------------------------------------ the draft --
  const drawPoint = pointer ? (pointer.inf?.point ?? pointer.raw) : null;
  const draft = toolOn && ctx && draw.start ? draftOf(draw, ctx, drawPoint, typedValues(typed)) : null;
  const draftSym = draft && rise ? stairSymbol({ runs: draft.layout.runs, landings: draft.layout.landings, location: opts.location, z_base: rise.zBase, z_top: rise.zTop, tread_depth: null }) : null;
  const count = draft && ctx ? readout(draw, ctx, draft) : null;
  const riser = rise && ctx ? (rise.zTop - rise.zBase) / ctx.total : null;
  const draftComfort = draft && riser !== null ? comfort(riser, draft.going) : null;

  // ------------------------------------------------------ status line --
  const gesture = useMemo<GestureStatus | null>(() => {
    if (drag?.moved && drag.result) {
      return {
        hint: { lead: `${stairLabel(drag.stair)} · slip for at sætte`, keys: [{ key: 'Alt', text: 'uden snap' }, { key: 'Esc', text: 'annullér' }] },
        readout: 'error' in drag.result ? drag.result.error : drag.result.readout,
      };
    }
    if (!toolOn) return null;
    if (!ctx) return { hint: { lead: 'Trappe · vælg en etage med en højde', keys: [{ key: 'Esc', text: 'tilbage til Vælg' }] }, readout: null };
    if (!draw.start) return { hint: toolHint('stair', 0), readout: riser !== null ? `${ctx.total} trin · ${cmText(riser)}` : null };
    if (draw.first === null) {
      const len = draft ? Math.hypot(draft.layout.runs[0].end[0] - draft.layout.runs[0].start[0], draft.layout.runs[0].end[1] - draft.layout.runs[0].start[1]) : 0;
      return {
        hint: {
          lead: count && count.left > 0 ? 'Trappe · klik for repos, eller træk videre' : 'Trappe · klik for at afslutte',
          keys: [
            { key: '0–9', text: 'antal trin' },
            { key: 'Tab', text: 'længde' },
            { key: '↵', text: 'sæt' },
            { key: 'Alt', text: 'uden snap' },
            { key: 'Esc', text: 'annullér' },
          ],
        },
        readout: count ? `løb ${fmt(len)} m · ${count.count.replace(' af ', '/').replace(' trin', '')}` : null,
      };
    }
    return {
      hint: {
        lead: opts.mode === 'straight' ? 'Trappe · klik for at sætte 2. løb' : 'Trappe · peg til siden, klik for at sætte 2. løb',
        keys: [...(opts.mode !== 'straight' ? [{ key: 'Mellemrum', text: 'vend' }] : []), { key: '⌫', text: 'tilbage' }, { key: 'Esc', text: 'annullér' }],
      },
      readout: count?.count ?? null,
    };
  }, [drag, toolOn, ctx, draw.start, draw.first, draft, count, riser, opts.mode]);
  useEffect(() => {
    // Only while this layer has something to say: another tool's layer owns the line otherwise.
    if (gesture || toolOn) editor.gesture.set(gesture);
  }, [editor.gesture, gesture, toolOn]);
  const hadGesture = useRef(false);
  useEffect(() => {
    if (hadGesture.current && !gesture && !toolOn) editor.gesture.set(null);
    hadGesture.current = gesture !== null || toolOn;
  }, [editor.gesture, gesture, toolOn]);

  // ------------------------------------------------------------- draw --
  const poly = (pts: readonly XY[]): string | null => {
    const out: string[] = [];
    for (const p of pts) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };

  const dragged = drag?.moved && drag.result && 'ops' in drag.result ? (applyStairOps([drag.stair], drag.result.ops)[0] ?? null) : null;
  const shownInf = drag?.moved ? drag.inf : toolOn && draw.first === null ? (pointer?.inf ?? null) : null;
  const infAt = shownInf && shownInf.kind !== 'free' ? toScreen(shownInf.point) : null;
  const infGuide = shownInf?.guide ? poly([shownInf.guide[0], shownInf.guide[1]]) : null;
  const pointerAt = drawPoint ? toScreen(drawPoint) : null;

  // The run being drawn: its length along the far side, its width across the start.
  const runDims: { id: string; from: XY; to: XY; value: number; live: boolean }[] = [];
  if (draft && draw.first === null) {
    const r = draft.layout.runs[0];
    const f = runFrame(r, opts.location);
    const out = f.lo + f.width + 0.35;
    const along = (s: number, w: number): XY => [f.p0[0] + f.d[0] * s + f.right[0] * w, f.p0[1] + f.d[1] * s + f.right[1] * w];
    if (f.len > 0.05) runDims.push({ id: 'len', from: along(0, out), to: along(f.len, out), value: f.len, live: true });
    runDims.push({ id: 'width', from: along(-0.35, f.lo), to: along(-0.35, f.lo + f.width), value: f.width, live: false });
  }

  // The flip control: beyond the selected stair's top.
  const flipAt = (() => {
    if (!selected || !canEdit || drag) return null;
    const sym = stairSymbol(selected);
    const tip = toScreen(sym.arrow.tip);
    const back = toScreen(sub(sym.arrow.tip, sym.arrow.dir));
    if (!tip || !back) return null;
    const dx = tip[0] - back[0];
    const dy = tip[1] - back[1];
    const L = Math.hypot(dx, dy) || 1;
    return { transform: `translate(${(tip[0] + (dx / L) * 26).toFixed(1)}px, ${(tip[1] + (dy / L) * 26).toFixed(1)}px) translate(-50%, -50%)` };
  })();

  // The wheel zooms the plan wherever the pointer is: over a grip or a control too.
  const forwardWheel = (e: ReactWheelEvent) => {
    const canvas = hostEl.current?.querySelector('canvas');
    if (!canvas || e.target === canvas) return;
    canvas.dispatchEvent(new WheelEvent('wheel', { ...e.nativeEvent, deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, clientX: e.clientX, clientY: e.clientY, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, bubbles: true, cancelable: true }));
  };

  const boxField = typed?.at ?? 0;
  const boxValue = typed?.fields[boxField];
  const liveBox = boxField === 0 ? (count ? count.count.replace(' trin', '') : '') : draft ? fmt(Math.hypot(draft.layout.runs[0].end[0] - draft.layout.runs[0].start[0], draft.layout.runs[0].end[1] - draft.layout.runs[0].start[1])) : '';

  return (
    <div ref={layerRef} className={styles.layer} onWheel={forwardWheel}>
      <svg className={styles.svg} aria-hidden="true">
        {/* Holes cut by stairs from the storey below. */}
        {arriving.map((s) =>
          stairFootprint(s).map((ring, i) => {
            const d = poly(ring);
            const a = ring.length === 4 ? poly([ring[0], ring[2]]) : null;
            const b = ring.length === 4 ? poly([ring[1], ring[3]]) : null;
            return d ? (
              <g key={`${s.guid}:${i}`} className={selectedSet.has(s.guid) ? own.on : undefined}>
                <polygon points={d} className={own.hole} />
                {a && <polyline points={a} className={own.holeCross} />}
                {b && <polyline points={b} className={own.holeCross} />}
              </g>
            ) : null;
          }),
        )}
        {storeyStairs.map((s) => {
          const shown = dragged && s.guid === dragged.guid ? dragged : s;
          return <StairShape key={s.guid} sym={stairSymbol(shown)} poly={poly} toScreen={toScreen} className={selectedSet.has(s.guid) || shown === dragged ? own.on : undefined} />;
        })}
        {draftSym && <StairShape sym={draftSym} poly={poly} toScreen={toScreen} className={`${own.on} ${own.draft}`} />}
        {runDims.map((dim) => {
          const a = toScreen(dim.from);
          const b = toScreen(dim.to);
          if (!a || !b) return null;
          const dx = b[0] - a[0];
          const dy = b[1] - a[1];
          const L = Math.hypot(dx, dy) || 1;
          const nx = (-dy / L) * 4;
          const ny = (dx / L) * 4;
          return (
            <g key={dim.id} className={styles.dim}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} />
              <line x1={a[0] - nx} y1={a[1] - ny} x2={a[0] + nx} y2={a[1] + ny} />
              <line x1={b[0] - nx} y1={b[1] - ny} x2={b[0] + nx} y2={b[1] + ny} />
            </g>
          );
        })}
        {infGuide && shownInf && <polyline points={infGuide} className={shownInf.kind === 'trace' ? `${styles.guide} ${styles.traceGuide}` : styles.guide} />}
        {draw.start && toolOn && (() => {
          const s = toScreen(draw.start);
          return s ? <circle cx={s[0]} cy={s[1]} r={3.5} className={styles.chainPoint} /> : null;
        })()}
        {selected &&
          grips.map((g) => {
            const s = toScreen(g.at);
            if (!s) return null;
            const on = drag?.grip.kind === g.kind;
            const cls = on ? `${styles.grip} ${styles.gripOn}` : styles.grip;
            const title = g.kind === 'end' ? 'Træk for at ændre grunden' : g.kind === 'first' ? 'Træk for at flytte reposen (trin mellem løbene)' : 'Træk for at ændre bredden';
            if (g.kind === 'width') {
              const t = toScreen([g.at[0] + g.axis[0], g.at[1] + g.axis[1]]);
              const angle = t ? (Math.atan2(t[1] - s[1], t[0] - s[0]) * 180) / Math.PI + 90 : 0;
              return (
                <rect key={g.kind} x={s[0] - 7} y={s[1] - 2.5} width={14} height={5} rx={2} transform={`rotate(${angle.toFixed(1)} ${s[0]} ${s[1]})`} className={cls} onPointerDown={(e) => startGrip(g, selected, e)}>
                  <title>{title}</title>
                </rect>
              );
            }
            return (
              <circle key={g.kind} cx={s[0]} cy={s[1]} r={5} className={cls} onPointerDown={(e) => startGrip(g, selected, e)}>
                <title>{title}</title>
              </circle>
            );
          })}
        {shownInf && <TrackLines tracks={shownInf.tracks} points={tracked} toScreen={toScreen} />}
        {infAt && shownInf && <Glyph kind={shownInf.kind} at={infAt} />}
      </svg>
      {runDims.map((dim) => {
        const at = toScreen([(dim.from[0] + dim.to[0]) / 2, (dim.from[1] + dim.to[1]) / 2]);
        return at ? (
          <span key={dim.id} className={dim.live ? styles.liveDim : styles.dimStatic} style={{ transform: `translate(${at[0].toFixed(1)}px, ${at[1].toFixed(1)}px) translate(-50%, -50%)` }}>
            {fmt(dim.value)}
          </span>
        ) : null;
      })}
      {infAt && shownInf?.tip && !drag && <ScreenTip inf={shownInf} at={infAt} />}
      {drag?.moved && drag.result && (() => {
        const at = toScreen(drag.inf?.point ?? drag.grip.at);
        if (!at) return null;
        const bad = 'error' in drag.result;
        return (
          <span className={bad ? `${styles.tip} ${styles.tipWarn}` : styles.liveDim} style={{ transform: `translate(${(at[0] + 14).toFixed(1)}px, ${(at[1] - 30).toFixed(1)}px)` }}>
            {'error' in drag.result ? drag.result.error : drag.result.readout}
          </span>
        );
      })()}
      {toolOn && draw.start && draft && pointerAt && count && (
        <span className={styles.measure} style={{ transform: `translate(${(pointerAt[0] + 16).toFixed(1)}px, ${(pointerAt[1] + 16).toFixed(1)}px)` }} role="status">
          <span className={styles.measureBox}>
            <small>{draw.first === null ? FIELDS[boxField] : 'Trin'}</small>
            <b>
              {draw.first === null && boxValue ? boxValue : <span className={styles.measureLive}>{draw.first === null ? liveBox : count.count.replace(' trin', '')}</span>}
              {draw.first === null && <u aria-hidden="true" />}
              {draw.first === null && boxField === 1 ? ' m' : ''}
            </b>
          </span>
          <span className={styles.measureCap}>
            {draw.first === null
              ? count.left > 0
                ? `${count.left} tilbage · klik for repos`
                : 'klik for at afslutte'
              : opts.mode === 'straight'
                ? 'klik for 2. løb'
                : 'Mellemrum vender'}
          </span>
          {draftComfort !== null && (
            <span className={`${styles.measureCap} ${comfortOk(draftComfort) ? '' : styles.tipWarn}`}>
              2s + g {cmText(draftComfort)}
              {comfortOk(draftComfort) ? '' : ' · uden for 59–65 cm'}
            </span>
          )}
        </span>
      )}
      {flipAt && (
        <span className={flips.flips} style={flipAt}>
          <button type="button" className={flips.flip} title="Vend gangretning (Mellemrum)" aria-label="Vend gangretning (Mellemrum)" onPointerDown={(e) => e.stopPropagation()} onClick={flipSelected}>
            <CadIcon name="flipFacing" className={flips.flipIcon} />
          </button>
        </span>
      )}
    </div>
  );
}

/** One stair's plan symbol, in screen space. */
function StairShape({ sym, poly, toScreen, className }: { sym: StairSymbol; poly: (pts: readonly XY[]) => string | null; toScreen: (p: XY) => [number, number] | null; className?: string }) {
  const tip = toScreen(sym.arrow.tip);
  const back = toScreen(sub(sym.arrow.tip, sym.arrow.dir));
  const start = toScreen(sym.start);
  let arrow: string | null = null;
  let up: { x: number; y: number } | null = null;
  if (tip && back) {
    const dx = tip[0] - back[0];
    const dy = tip[1] - back[1];
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    const k = 9;
    arrow = `${tip[0].toFixed(1)},${tip[1].toFixed(1)} ${(tip[0] - ux * k - uy * k * 0.45).toFixed(1)},${(tip[1] - uy * k + ux * k * 0.45).toFixed(1)} ${(tip[0] - ux * k + uy * k * 0.45).toFixed(1)},${(tip[1] - uy * k - ux * k * 0.45).toFixed(1)}`;
  }
  if (start && sym.walk.length > 1) {
    const s1 = toScreen(sym.walk[1]);
    if (s1) {
      const dx = s1[0] - start[0];
      const dy = s1[1] - start[1];
      const L = Math.hypot(dx, dy) || 1;
      // "OP" beside the walking line, a little up the run.
      up = { x: start[0] + (dx / L) * 16 + (-dy / L) * 9, y: start[1] + (dy / L) * 16 + (dx / L) * 9 };
    }
  }
  const cut = sym.cut ? (() => {
    const a = toScreen(sym.cut[0]);
    const b = toScreen(sym.cut[1]);
    if (!a || !b) return null;
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = Math.hypot(dx, dy) || 1;
    const px = (-dy / L) * 4;
    const py = (dx / L) * 4;
    const ax = (dx / L) * 3;
    const ay = (dy / L) * 3;
    return `${a[0].toFixed(1)},${a[1].toFixed(1)} ${(mx - ax + px).toFixed(1)},${(my - ay + py).toFixed(1)} ${(mx + ax - px).toFixed(1)},${(my + ay - py).toFixed(1)} ${b[0].toFixed(1)},${b[1].toFixed(1)}`;
  })() : null;
  const walk = poly(sym.walk);
  return (
    <g className={className}>
      {sym.landings.map((l, i) => {
        const d = poly(l);
        return d ? <polygon key={`l${i}`} points={d} className={own.landing} /> : null;
      })}
      {sym.runs.map((r, i) => {
        const d = poly(r.rect);
        return (
          <g key={`r${i}`}>
            {d && <polygon points={d} className={own.run} />}
            {r.risers.map((line, j) => {
              const a = toScreen(line[0]);
              const b = toScreen(line[1]);
              return a && b ? <line key={j} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className={r.above[j] ? own.riserAbove : own.riser} /> : null;
            })}
          </g>
        );
      })}
      {walk && <polyline points={walk} className={own.walk} />}
      {cut && <polyline points={cut} className={own.breakLine} />}
      {start && <circle cx={start[0]} cy={start[1]} r={3} className={own.dot} />}
      {arrow && <polygon points={arrow} className={own.arrow} />}
      {up && (
        <text x={up.x} y={up.y} className={own.up} textAnchor="middle" dominantBaseline="middle">
          OP
        </text>
      )}
    </g>
  );
}
